import { configuration } from '../Configuration';

/**
 * Sign-in with Microsoft Entra ID — the portal half (server contract: tri-server docs/entra-sso.md).
 *
 * The nonce is what proves that *this* browser started the sign-in: the server binds the one-time
 * code to it, and only this tab's sessionStorage has it. A code planted through a link (login CSRF)
 * therefore fails in the victim's browser. sessionStorage, not localStorage, so another tab or a
 * later visit cannot redeem it.
 */
const STORAGE_KEY = 'tri.entraSso';
const NOTICE_KEY = 'tri.ssoSessionNotice';

type PendingSignIn = { nonce: string; redirect: string | null };

const randomNonce = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/** Leaves the page for Microsoft. `redirect` is where to land afterwards (already sanitized). */
export const startEntraSignIn = (email: string, redirect: string | null) => {
  const pending: PendingSignIn = { nonce: randomNonce(), redirect };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  const params = new URLSearchParams({
    origin: window.location.origin,
    nonce: pending.nonce,
    login_hint: email,
  });
  window.location.assign(`${configuration.serverUrl}/auth/entra/start?${params.toString()}`);
};

/** Read once: the pending sign-in is removed as it is taken. */
export const takePendingEntraSignIn = (): PendingSignIn | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingSignIn) : null;
  } catch {
    return null;
  }
};

/** The server's refusal codes (`SsoRefusal` plus `expired`/`microsoft`) → a translation key. */
export const entraErrorKey = (code: string | null): string => {
  switch (code) {
    case 'tenant_not_enabled':
      return 'entraSso.tenantNotEnabled';
    case 'user_not_found':
    case 'user_ambiguous':
      return 'entraSso.userNotFound';
    case 'user_inactive':
      return 'entraSso.userInactive';
    case 'expired':
      return 'entraSso.expired';
    default:
      return 'entraSso.failed';
  }
};

/**
 * The server ends the session of a customer with SSO only (`sso_required`, `sso_session_expired`),
 * and the refresh fails with that reason. Kept for the login page the user lands on next.
 */
export const noteSsoSessionEnd = (reason: unknown) => {
  if (reason !== 'sso_required' && reason !== 'sso_session_expired') return;
  try {
    sessionStorage.setItem(NOTICE_KEY, reason);
  } catch {
    // No storage, no hint — the login page still offers Microsoft.
  }
};

/** Read by the login page; it clears the notice after mounting (`clearSsoSessionNotice`). */
export const readSsoSessionNotice = (): string | null => {
  try {
    return sessionStorage.getItem(NOTICE_KEY);
  } catch {
    return null;
  }
};

/**
 * Separate from reading: StrictMode runs a `useState` initializer twice, and a read that also
 * removed the notice left the second run — the one React keeps — empty.
 */
export const clearSsoSessionNotice = () => {
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // Nothing stored then.
  }
};

/**
 * The confirmation after creating a user: for an organization that signs in only with Microsoft
 * the server sends no invitation (`ssoOnly: true`) — the user signs in with Microsoft right away.
 */
export const createdUserMessageKey = (result: { ssoOnly?: boolean } | null | undefined) =>
  result?.ssoOnly
    ? 'settingsPanel.userManagementPanel.userCreatedSsoOnly'
    : 'settingsPanel.userManagementPanel.userInvited';
